import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Article_Carousel.css';

const ArticleCarousel = () => {
  const articles = [
    {
        id: 1,
        title: 'Why Machines Can\'t Act From Ethics',
        image: '/images/article-images/machine-ethics.jpg',
        link: '/thought-leadership/machine-ethics',
      },
      {
        id: 2,
        title: 'Against Precautionary Basic Income: An Argument for the Survival of Survival',
        image: '/images/article-images/survival.jpg',
        link: '/thought-leadership/precautionary-basic-income',
      },
      {
        id: 3,
        title: 'Hacking Inductive Authenticity',
        image: '/images/article-images/hacking-authenticity.webp',
        link: '/thought-leadership/hacking-authenticity',
      },
      {
          id: 4,
          title: 'Continuity and Consciousness',
          image: '/images/article-images/consciousness.jpg',
          link: '/thought-leadership/continuity-consciousness',
      },
      {
          id: 5,
          title: 'The Easy Problem of AI Rights',
          image: '/images/article-images/ai-rights.jpg',
          link: '/thought-leadership/ai-rights',
      },
      {
          id: 6,
          title: 'Machine God: Theōria Superī',
          image: '/images/article-images/machine-god.webp',
          link: '/thought-leadership/machine-god',
      },
      {
          id: 7,
          title: 'How Often Do Men Think About the Roman Empire? Misconceptions of Roman Masculinity',
          image: '/images/article-images/roman-masculinity.jpg',
          link: '/thought-leadership/roman-masculinity',
      },
      {
          id: 8,
          title: 'Can teachers detect ChatGPT usage?',
          image: '/images/article-images/chat.jpg',
          link: '/thought-leadership/chat',
      },
      {
          id: 9,
          title: 'Mathematical Investigation of the Buoyant Force in Non-Newtonian Fluids',
          image: '/images/article-images/buoyancy.png',
          link: '/thought-leadership/fluids',
      },
      {
          id: 10,
          title: 'Stock Prediction with Difference Equations and Custom Sentiment Analysis',
          image: '/images/article-images/stocks.jpg',
          link: '/thought-leadership/stocks',
      },
  ];

  const GAP = 20;
  const MIN_CARD = 280;

  const scrollRef = useRef(null);
  const containerRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardWidth, setCardWidth] = useState(300);
  const [visibleCards, setVisibleCards] = useState(3);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    // Size the cards so a whole page fits exactly, with no half-card peeking out.
    const measure = () => {
      if (!scrollRef.current) return;
      const rowWidth = scrollRef.current.clientWidth;
      const perPage = Math.max(
        1,
        Math.min(articles.length, Math.floor((rowWidth + GAP) / (MIN_CARD + GAP)))
      );
      setVisibleCards(perPage);
      setCardWidth((rowWidth - GAP * (perPage - 1)) / perPage);
      setTotalPages(Math.ceil(articles.length / perPage));
    };

    measure();

    const handleResize = () => {
      measure();
      setCurrentIndex(0);
      if (scrollRef.current) {
        scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [articles.length]);

  const pageWidth = () => cardWidth * visibleCards + GAP * visibleCards;

  const scrollLeft = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      scrollRef.current.scrollTo({
        left: newIndex * pageWidth(),
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (currentIndex < totalPages - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      scrollRef.current.scrollTo({
        left: newIndex * pageWidth(),
        behavior: 'smooth'
      });
    }
  };

  const goToPage = (pageIndex) => {
    setCurrentIndex(pageIndex);
    scrollRef.current.scrollTo({
      left: pageIndex * pageWidth(),
      behavior: 'smooth'
    });
  };

  return (
    <div className="ac-wrap">
      <div className="ac-row-container" ref={containerRef}>
        <button
          type="button"
          className="ac-arrow"
          onClick={scrollLeft}
          disabled={currentIndex === 0}
          aria-label="Previous articles"
        >
          &#8249;
        </button>

        <div className="ac-row" ref={scrollRef}>
          {articles.map((article) => (
            <Link
              key={article.id}
              to={article.link}
              className="ac-card"
              style={{ flex: `0 0 ${cardWidth}px`, width: `${cardWidth}px` }}
              data-card
            >
              <img src={article.image} alt={article.title} />
              <div className="ac-card-body">
                <h3>{article.title}</h3>
                <span className="ac-card-cta">
                  Read <span aria-hidden="true">&rarr;</span>
                </span>
              </div>
            </Link>
          ))}
        </div>

        <button
          type="button"
          className="ac-arrow"
          onClick={scrollRight}
          disabled={currentIndex === totalPages - 1}
          aria-label="More articles"
        >
          &#8250;
        </button>
      </div>

      {totalPages > 1 && (
        <div className="ac-dots">
          {Array.from({ length: totalPages }, (_, index) => (
            <button
              key={index}
              type="button"
              className={`ac-dot ${index === currentIndex ? 'is-active' : ''}`}
              onClick={() => goToPage(index)}
              aria-label={`Go to page ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ArticleCarousel;
